import { useEffect, useMemo, useRef, useState } from "react";

import { Formik } from "formik";

import { useDispatch, useSelector } from "react-redux";

import { followUp } from "../../../store/campaignBuilderSlice";

import { getCampaignBuilderDefaults } from "../../../utils/campaignBuilderDefaults";

import {

    MAX_OPPORTUNITIES,

    getFollowUpOpportunities,

    removeOpportunity,

} from "../../../utils/opportunitySelection";

import {
    isFollowUpStepValid,
    isOpportunitySectionOptional,
    isT3DetailsOptional,
    parseToneSelections,
    serializeTones,
    validateFollowUp,
    MAX_TONES,
    TONE_OPTIONS,
} from "../../../utils/validateFollowUp";
import { countWords, MAX_T3_FIELD_WORDS } from "../../../utils/textFieldValidation";
import { WIZARD_HASHTAGS_ENABLED } from "../../../constants/campaignWizard";
import Input from "./Input";

import OpportunityPickerModal from "./OpportunityPickerModal";

import OpportunityQuickPick from "./OpportunityQuickPick";

import SelectedOpportunityPreview from "./SelectedOpportunityPreview";

import "bootstrap-icons/font/bootstrap-icons.css";

import {
    trackCtaClicked,
    trackItemSelected,
} from "../../../analytics/analytics";
import { useScrolled } from "../../../analytics/analytics.hooks";
import {
    ANALYTICS_ITEM_TYPES,
    ANALYTICS_SECTIONS,
    ANALYTICS_CTAS,
} from "../../../analytics/analytics.constants";

function FollowUpInfoAlert() {

    return (

        <div
            className="mb-4 flex gap-2 rounded-lg border border-amber-200/80 bg-amber-50 px-3 py-2.5 md:mb-5 md:gap-2.5 md:px-3.5"
            role="note"
        >
            <i className="bi bi-info-circle mt-0.5 shrink-0 text-sm text-amber-600" aria-hidden />
            <p className="mb-0 text-xs leading-relaxed text-amber-950/85 md:text-sm">
                When we create your posts, your topic is hooked with the selected trends for maximum engagement.
            </p>
        </div>

    );

}

function TonePillPicker({ selected = [], onChange, disabled = false }) {
    const selectedSet = new Set(selected);
    const isMaxReached = selected.length >= MAX_TONES;

    const handleToggle = (option) => {
        if (disabled) return;
        if (selectedSet.has(option)) {
            onChange(selected.filter((item) => item !== option));
            return;
        }
        if (isMaxReached) return;
        onChange([...selected, option]);
    };

    return (
        <div>
            <div className="flex flex-wrap gap-2">
                {TONE_OPTIONS.map((option) => {
                    const isSelected = selectedSet.has(option);
                    const isDisabled = disabled || (isMaxReached && !isSelected);

                    return (
                        <button
                            key={option}
                            type="button"
                            onClick={() => handleToggle(option)}
                            disabled={isDisabled}
                            aria-pressed={isSelected}
                            className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                                isSelected
                                    ? "bg-slate-900 text-white"
                                    : "bg-slate-100 text-slate-700 hover:bg-slate-200/80 hover:text-slate-900"
                            } ${isDisabled ? "cursor-not-allowed opacity-40" : ""}`}
                        >
                            {option}
                        </button>
                    );
                })}
            </div>
            <p className="mt-2 text-xs text-slate-500 md:text-sm">
                {selected.length}/{MAX_TONES} selected
            </p>
        </div>
    );
}



function T3DetailsFields({

    values,

    errors,

    touched,

    handleChange,

    handleBlur,

    setFieldValue,

    setFieldTouched,

    onValidityChange,

    readOnly = false,

    fastForward = false,

    onFastForwardHandled,

}) {

    const dispatch = useDispatch();
    const [pickerOpen, setPickerOpen] = useState(false);

    const opportunitiesRef = useRef(null);
    const toneRef = useRef(null);

    const topicRef = useRef(null);
    const topicInputRef = useRef(null);
    const fastForwardHandledRef = useRef(false);

    const targetRef = useRef(null);

    const sectionEnabled = !readOnly;



    useScrolled(opportunitiesRef, ANALYTICS_SECTIONS.OPPORTUNITIES, sectionEnabled);
    useScrolled(toneRef, ANALYTICS_SECTIONS.TONE, sectionEnabled);
    useScrolled(topicRef, ANALYTICS_SECTIONS.TOPIC, sectionEnabled);
    useScrolled(targetRef, ANALYTICS_SECTIONS.TARGET, sectionEnabled);

    const opportunityOptional = isOpportunitySectionOptional(values);
    const t3Optional = isT3DetailsOptional(values);

    const stepValid = isFollowUpStepValid(values);



    useEffect(() => {

        onValidityChange?.(readOnly ? false : stepValid);

    }, [stepValid, onValidityChange, readOnly]);

    useEffect(() => {
        if (!fastForward || readOnly || fastForwardHandledRef.current) return;
        fastForwardHandledRef.current = true;
        onFastForwardHandled?.();

        const hasTrends = Array.isArray(values.opportunities) && values.opportunities.length > 0;
        if (!hasTrends) {
            setPickerOpen(true);
            return;
        }

        const focusTimer = window.setTimeout(() => {
            topicInputRef.current?.focus?.();
        }, 450);

        return () => window.clearTimeout(focusTimer);
    }, [fastForward, readOnly, values.opportunities, onFastForwardHandled]);



    useEffect(() => {

        if (readOnly) return;

        dispatch(
            followUp({
                ...values,
                tone: serializeTones(values.tone),
                hashtags: WIZARD_HASHTAGS_ENABLED ? values.hashtags : [],
            })
        );

    }, [values, dispatch, readOnly]);

    const handleOpportunityChange = (next) => {

        const previousIds = new Set(values.opportunities.map((item) => item.id));

        const added = next.find((item) => !previousIds.has(item.id));



        if (added) {

            const position = next.findIndex((item) => item.id === added.id) + 1;

            trackItemSelected(
                ANALYTICS_ITEM_TYPES.OPPORTUNITY,
                added.id,
                position
            );

        }



        setFieldValue("opportunities", next);

    };



    const handleViewAllOpportunities = () => {
        trackCtaClicked(ANALYTICS_CTAS.VIEW_ALL_OPPORTUNITIES);
        setPickerOpen(true);
    };



    const renderWordCount = (fieldName, value) => {

        const count = countWords(value);

        const overLimit = count > MAX_T3_FIELD_WORDS;

        return (

            <p

                className={`mt-1 text-xs md:text-sm ${overLimit && touched[fieldName] ? "text-red-600" : "text-slate-500"

                    }`}

            >

                {count}/{MAX_T3_FIELD_WORDS} words

            </p>

        );

    };



    return (

        <div className="rounded-xl p-1 md:p-5">

            <div ref={topicRef} className="form-section mb-8 space-y-2.5 md:mb-10">

                <h5 className="mb-1 flex items-center gap-2 text-base fw-semibold text-slate-900 md:gap-2.5 md:text-lg">

                    <i className="bi bi-hash text-slate-500" aria-hidden />

                    What do you want to post about? (optional)

                </h5>

                <Input

                    name="topic"

                    type="text"

                    placeholder="— exam tips, hotel offers, viral trends, or festivals"

                    value={values.topic}

                    onChange={handleChange}

                    onBlur={handleBlur}

                    disabled={readOnly}

                    ref={topicInputRef}

                />

                {touched.topic && errors.topic ? (

                    <p className="mt-1 text-xs text-red-600 md:text-sm">{errors.topic}</p>

                ) : (

                    renderWordCount("topic", values.topic)

                )}

            </div>

            <div ref={opportunitiesRef} className="form-section mb-8 space-y-2.5 md:mb-10">

                <h5 className="mb-1 flex items-center gap-2 text-base fw-semibold text-slate-900 md:gap-2.5 md:text-lg">

                    <i className="bi bi-fire text-orange-500" aria-hidden />

                    Trends

                </h5>

                <label className="form-label mb-3 text-sm text-slate-600 md:text-base">

                    {opportunityOptional

                        ? `Optional — pick up to ${MAX_OPPORTUNITIES} marketing trends or browse all`
                        : `Pick at least one trend, or fill tone, subject & target below — up to ${MAX_OPPORTUNITIES}`}
                </label>

                {!readOnly ? (

                    <OpportunityQuickPick

                        selected={values.opportunities}

                        onChange={handleOpportunityChange}

                        onViewAll={handleViewAllOpportunities}

                    />

                ) : null}

                {values.opportunities.length > 0 ? (

                    <div className={readOnly ? "" : "mt-4"}>

                        <SelectedOpportunityPreview

                            opportunities={values.opportunities}

                            onRemove={(id) =>

                                setFieldValue(

                                    "opportunities",

                                    removeOpportunity(values.opportunities, id)

                                )

                            }

                            readOnly={readOnly}

                        />

                    </div>

                ) : null}

                {!readOnly && pickerOpen ? (

                    <OpportunityPickerModal

                        selected={values.opportunities}

                        onChange={handleOpportunityChange}

                        onClose={() => setPickerOpen(false)}

                    />

                ) : null}

                {touched.opportunities && errors.opportunities ? (

                    <p className="mt-1 text-xs text-red-600 md:text-sm">{errors.opportunities}</p>

                ) : null}

            </div>

            <div ref={toneRef} className="form-section mb-8 space-y-2.5 md:mb-10">

                <h5 className="mb-1 flex items-center gap-2 text-base fw-semibold text-slate-900 md:gap-2.5 md:text-lg">

                    <i className="bi bi-chat-heart text-slate-500" aria-hidden />

                    Tone

                </h5>

                <label className="form-label mb-2 text-sm text-slate-600 md:text-base">

                    {t3Optional

                        ? `Optional — pick up to ${MAX_TONES} tones for your posts`

                        : `Pick up to ${MAX_TONES} tones for your posts`}

                </label>

                <TonePillPicker
                    selected={parseToneSelections(values.tone)}
                    onChange={(next) => {
                        setFieldValue("tone", next);
                        setFieldTouched("tone", true, false);
                    }}
                    disabled={readOnly}
                />

                {touched.tone && errors.tone ? (

                    <p className="mt-1 text-xs text-red-600 md:text-sm">{errors.tone}</p>

                ) : null}

            </div>



            {/* <div ref={targetRef} className="form-section mb-8 space-y-2.5 md:mb-10">

                <h5 className="mb-1 flex items-center gap-2 text-base fw-semibold text-slate-900 md:gap-2.5 md:text-lg">

                    <i className="bi bi-bullseye text-slate-500" aria-hidden />

                    Target

                </h5>

                <label className="form-label mb-2 text-sm text-slate-600 md:text-base">

                    {t3Optional

                        ? "Optional — defines who you're speaking to"

                        : "Defines who you're speaking to"}

                </label>

                <Input

                    name="target"

                    type="text"

                    placeholder="— students, travelers, foodies, or parents"

                    value={values.target}

                    onChange={handleChange}

                    onBlur={handleBlur}

                    disabled={readOnly}

                />

                {touched.target && errors.target ? (

                    <p className="mt-1 text-xs text-red-600 md:text-sm">{errors.target}</p>

                ) : (

                    renderWordCount("target", values.target)

                )}

            </div> */}

        </div >

    );

}



const T3Details = ({ onValidityChange, readOnly = false, fastForward = false, onFastForwardHandled }) => {

    const userId = useSelector((state) => state.auth.user?.uid);

    const stored = useSelector((state) => state.campaignBuilder.followUp);

    const defaults = useMemo(() => getCampaignBuilderDefaults(userId), [userId]);



    const storedOpportunities = useMemo(

        () => getFollowUpOpportunities(stored),

        [stored]

    );



    const initialValues = useMemo(

        () => ({

            tone: parseToneSelections(stored.tone),

            topic: stored.topic ?? "",

            target: stored.target || defaults.followUp.target,

            hashtags: WIZARD_HASHTAGS_ENABLED ? (stored.hashtags ?? []) : [],

            opportunities: storedOpportunities,

        }),

        [stored.tone, stored.topic, stored.target, stored.hashtags, storedOpportunities, defaults]

    );



    return (

        <div className="col-md-12">

            <div className="w-full px-0 py-2 md:px-2 md:py-3">

                <h4 className="mb-3 flex items-center gap-2 text-xl font-bold text-slate-900 md:mb-4 md:gap-3 md:text-2xl">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 md:h-10 md:w-10">
                        <i className="bi bi-chat-left-dots text-lg md:text-xl" aria-hidden />
                    </span>
                    Shape the campaign
                </h4>



                <FollowUpInfoAlert />



                <Formik

                    initialValues={initialValues}

                    validate={readOnly ? undefined : validateFollowUp}

                    validateOnMount={!readOnly}

                    validateOnChange={!readOnly}

                    validateOnBlur={!readOnly}

                    enableReinitialize

                    onSubmit={() => { }}

                >

                    {(formikProps) => (

                        <T3DetailsFields

                            {...formikProps}

                            readOnly={readOnly}

                            onValidityChange={onValidityChange}

                            fastForward={fastForward}

                            onFastForwardHandled={onFastForwardHandled}

                        />

                    )}

                </Formik>

            </div>

        </div>

    );

};



export default T3Details;


